import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ListFooter } from '../ui';
import { nearMatches } from '../utils/nearMatch';
import { askReason } from '../utils/reportError';

// Finance · PDC (post-dated cheque) Register.
// Multi-tenant: a company switcher drives the X-Company-Id header on every request.
// Register only — no forecast hook / alert / importer (next session).

const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
// ── CHEQUE-DATE ARITHMETIC ────────────────────────────────────────────────────
// CALENDAR days, deliberately (ruled 2026-08-31). The register shows no weekend rule to
// honour: 15 of 131 cheques fall on a Sunday and Saturday is the most common due day (40),
// so shifting off a weekend would encode a practice the data contradicts.
//
// Built in UTC from the parts rather than with local-time Date maths — a date-only string
// parsed as local time then shifted by N days can cross a DST boundary and land a day out.
// That is the same class of bug the pg DATE handling fix addressed on the server side.
const calcChequeDate = (invoiceDate, days) => {
  if (!invoiceDate || days === '' || days == null) return null;
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return null;
  const [y, m, d] = String(invoiceDate).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const t = Date.UTC(y, m - 1, d) + n * 86400000;
  return new Date(t).toISOString().slice(0, 10);
};
// The inverse: how many days between the invoice and a chosen cheque date. Same UTC
// construction, so the pair round-trips exactly — days -> date -> days returns what was typed.
const daysBetween = (invoiceDate, chequeDate) => {
  if (!invoiceDate || !chequeDate) return null;
  const [y1, m1, d1] = String(invoiceDate).slice(0, 10).split('-').map(Number);
  const [y2, m2, d2] = String(chequeDate).slice(0, 10).split('-').map(Number);
  if (!y1 || !m1 || !d1 || !y2 || !m2 || !d2) return null;
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
};
const fmtLong = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d))
    .toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const STATUS_STYLE = {
  Pending:        { bg: '#FAEEDA', c: '#854F0B' },
  Cleared:        { bg: '#DCFCE7', c: '#166534' },
  Bounced:        { bg: '#FCEBEB', c: '#A32D2D' },
  Cancelled:      { bg: '#EEE', c: '#666' },
  stop_requested: { bg: '#FCE7E7', c: '#A32D2D' },
  // `stopped` is the BANK's confirmation that the money did not leave (mig 345). Green, because it
  // is a good outcome — and visually distinct from stop_requested, which is still an open question.
  stopped:        { bg: '#DCFCE7', c: '#14532D' },
};
const STATUS_LABEL = { stop_requested: 'Stop Requested', stopped: 'Stopped — bank confirmed' };

// RULING 24 (mig 350). The approval state is a STATE, not a tick: there are two legitimate ways to
// be approved and four distinct ways not to be, and collapsing them to yes/no would hide the one
// that matters — a cheque LINKED to a request whose approval does not hold up looks approved and is
// not. Read from pdc_approval_kind() on the row; never derived here from approved_by.
const APPROVAL = {
  approved_directly:          { label: 'Approved',            bg: '#E3F3E6', c: '#1E6B33', hint: 'Approved on the cheque by a named person other than whoever issued it.' },
  approved_via_request:       { label: 'Approved via request', bg: '#E3F3E6', c: '#1E6B33', hint: 'Approved through the payment request this cheque executes — one act, and it was performed there.' },
  unapproved:                 { label: 'Not approved',        bg: '#F3F1EC', c: '#7A6A46', hint: 'Nobody has approved this payment. Recording it is not required yet (ruling 35).' },
  linked_request_unapproved:  { label: 'Linked, unapproved',   bg: '#FDF4E3', c: '#8A5A12', hint: 'Linked to a payment request that carries no approval. A link is not an approval.' },
};
// The *_invalid: forms carry segregation_kind()'s verdict after a colon. Rendered generically rather
// than enumerated, so a new verdict in the database cannot produce a blank cell here.
const approvalOf = (k) => APPROVAL[k] || (String(k || '').startsWith('own_approval_invalid')
  ? { label: 'Approval invalid', bg: '#FDECEA', c: '#A33', hint: 'An approval is recorded but does not hold up: ' + String(k).split(':')[1] }
  : String(k || '').startsWith('linked_request_approval_invalid')
  ? { label: 'Link does not approve', bg: '#FDECEA', c: '#A33', hint: 'Linked to a request whose own approval does not hold up (' + String(k).split(':')[1] + '), so ruling 24 is not satisfied by it.' }
  : { label: k || '—', bg: '#F3F1EC', c: '#777', hint: '' });
const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
// invoice_date / terms_* / due_date_touched are TRANSIENT — they drive the prefill and are not
// sent to the server (see the save payload, which names its fields explicitly).
// po_no is PERSISTED — it is the invoice link and the reason no invoice_date column is needed.
// invoice_date / terms_* / due_date_touched are transient: they drive the prefill, and the date
// is recoverable afterwards from po_no -> purchase_invoices.invoice_date.
const emptyForm = { cheque_no: '', party_name: '', amount: '', bank_account: '', issue_date: '', due_date: '', remarks: '',
                    po_no: '', invoice_date: '', terms_days: '', terms_basis: '', terms_supplier: '', due_date_touched: false };

// Party-exposure summary strip — one per payee_id group. Two large cards (Cleared / Balance),
// then the four exclusive ageing buckets (overdue danger · 0-15 warning · 16-30 & >30 neutral).
// Stopped + Cancelled are shown aside (excluded from balance + buckets by the server).
function PartyStrip({ g }) {
  const b = g.balance_to_clear, cl = g.cleared, bk = g.buckets;
  const card = (label, o, bg, c) => (
    <div style={{ flex: 1, minWidth: 190, background: bg, borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: c, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: c, marginTop: 2 }}>{inr(o.total)}</div>
      <div style={{ fontSize: 11, color: c, opacity: 0.8 }}>{o.count} cheque{o.count === 1 ? '' : 's'}</div>
    </div>
  );
  const bucket = (label, o, bg, c) => (
    <div style={{ flex: 1, minWidth: 120, background: bg, borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: c, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: c }}>{inr(o.total)}</div>
      <div style={{ fontSize: 10, color: c, opacity: 0.75 }}>{o.count} chq</div>
    </div>
  );
  return (
    <div style={{ marginBottom: 14, border: '1px solid #E7E5F0', borderRadius: 12, padding: 14, background: '#FCFCFF' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#2D2C72', marginBottom: 10 }}>
        {g.party_label}
        {g.stopped.count > 0 && <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 600, color: '#A32D2D', background: '#FCE7E7', padding: '2px 8px', borderRadius: 8 }}>Stopped {inr(g.stopped.total)} ({g.stopped.count})</span>}
        {g.cancelled.count > 0 && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#666', background: '#EEE', padding: '2px 8px', borderRadius: 8 }}>Cancelled {inr(g.cancelled.total)} ({g.cancelled.count})</span>}
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        {card('Cleared to date', cl, '#EAF7EE', '#166534')}
        {card('Balance to clear', b, '#EEEDF8', '#3B3A8F')}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {bucket('Overdue', bk.overdue, '#FCEBEB', '#A32D2D')}
        {bucket('0-15 days', bk.d0_15, '#FAEEDA', '#854F0B')}
        {bucket('16-30 days', bk.d16_30, '#F3F4F6', '#555')}
        {bucket('Beyond 30', bk.beyond_30, '#F3F4F6', '#555')}
      </div>
    </div>
  );
}

// Party-wise exposure table (unfiltered view) — one row per party, sorted by balance-to-clear
// desc (server-sorted). Click a row to filter the register to that party (single-party strip
// then takes over). Same colour language as the strip: balance indigo, overdue red.
function PartyTable({ parties, onPick }) {
  const th = { padding: '8px 10px', textAlign: 'right', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap', textAlign: 'right' };
  return (
    <div style={{ marginBottom: 14, border: '1px solid #E7E5F0', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#2D2C72', padding: '10px 14px', background: '#F5F4FF' }}>
        Party-wise exposure ({parties.length}) — largest balance first · click a party to drill in
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#FCFCFF' }}>
            {['Party', 'Cleared', 'Balance to clear', 'Overdue', '0-15', '16-30', 'Beyond 30'].map((h, i) => (
              <th key={h} style={{ ...th, textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {parties.map(g => (
              <tr key={g.party_label} onClick={() => onPick(g.party_label)} title="Filter the register to this party"
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F7F6FF')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#2D2C72' }}>{g.party_label}
                  {g.stopped.count > 0 && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: '#A32D2D' }}>· stop {inr(g.stopped.total)}</span>}
                </td>
                <td style={{ ...td, color: '#166534' }}>{inr(g.cleared.total)}</td>
                <td style={{ ...td, fontWeight: 800, color: '#3B3A8F' }}>{inr(g.balance_to_clear.total)}</td>
                <td style={{ ...td, color: g.buckets.overdue.total > 0 ? '#A32D2D' : '#999' }}>{inr(g.buckets.overdue.total)}</td>
                <td style={{ ...td, color: '#854F0B' }}>{inr(g.buckets.d0_15.total)}</td>
                <td style={td}>{inr(g.buckets.d16_30.total)}</td>
                <td style={td}>{inr(g.buckets.beyond_30.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PDCRegister() {
  const [companies, setCompanies] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [company, setCompany] = useState('');
  const [banks, setBanks] = useState([]);
  const [payees, setPayees] = useState([]);
  const [rows, setRows] = useState([]);
  const [filt, setFilt] = useState({ status: '', party: '', due_from: '', due_to: '' });
  const [modal, setModal] = useState(null);     // form object being added/edited (has .id when editing)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // ── cheque-date calculator (panel) ──
  // BIDIRECTIONAL (ruled 2026-08-31). Two inputs and one derived value, and which is which
  // depends on what was typed last: type days and the cheque date computes; type a cheque date
  // and the days compute. Those are the two questions people actually ask — "when is it due"
  // and "how many days is this one", and the second is the one the register could never answer.
  // `drive` records the side the user last touched; the OTHER side is the derived one.
  const [calc, setCalc] = useState({
    payee_id: '', invoice_date: '', days: '', cheque_date: '',
    drive: 'days', basis: '', supplier: '', touched: false,
  });

  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);

  useEffect(() => {
    api.get('/finance/pdc/companies').then(r => {
      setCompanies(r.data.data || []);
      setCanSwitch(r.data.can_switch);
      setCompany(r.data.active || (r.data.data || [])[0]?.id || '');
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!company) return;
    const p = new URLSearchParams();
    Object.entries(filt).forEach(([k, v]) => { if (v) p.append(k, v); });
    api.get('/finance/pdc?' + p.toString(), H()).then(r => setRows(r.data.data || [])).catch(() => setRows([]));
  }, [company, filt, H]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (company) api.get('/finance/pdc/bank-accounts', H()).then(r => setBanks(r.data.data || [])).catch(() => {}); }, [company, H]);

  // ── RULING 24 + RULING 35 ──────────────────────────────────────────────────────────────────
  // The approving roles are management / finance_approver / admin and deliberately NOT accounts:
  // accounts is the desk that executes, and a desk approving its own execution is what ruling 24
  // exists to stop. This mirrors PDC_APPROVE on the server; the server is the authority.
  const { user } = useAuth();
  const canApprove = ['admin', 'management', 'finance_approver'].includes(user?.role);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const loadApprovalStatus = useCallback(() => {
    if (!company) return;
    api.get('/finance/pdc/approval-status', H())
      .then(r => setApprovalStatus(r.data)).catch(() => setApprovalStatus(null));
  }, [company, H]);
  useEffect(() => { loadApprovalStatus(); }, [loadApprovalStatus]);

  const approve = async (row) => {
    const reason = window.prompt(
      `Approve payment of ${inr(row.amount)} to ${row.party_name}?\n\nCheque ${row.cheque_no || '(no number)'}.\n\n`
      + 'Why is this payment authorised? Optional, and kept verbatim.');
    if (reason === null) return;   // cancelled — an empty string is a deliberate "no comment"
    setErr('');
    try {
      await api.post(`/finance/pdc/${row.id}/approve`, { reason }, H());
      load(); loadApprovalStatus();
    } catch (e) {
      // The server's sentence is shown as it is. It carries the cause, the absolution and the
      // remedy (mig 347/348), and rewording it here would put a second copy of the rule on the
      // screen most likely to drift from it.
      setErr(e.response?.data?.error || e.message);
    }
  };

  // Party-exposure summary — ALWAYS fetched (party set → single-party strip; party blank → the
  // whole register: total tiles + party-wise table). Respects company + the due-date range filter.
  const [summary, setSummary] = useState({ parties: [], total: null, party_count: 0 });
  useEffect(() => {
    if (!company) { setSummary({ parties: [], total: null, party_count: 0 }); return; }
    const p = new URLSearchParams();
    if (filt.party.trim()) p.set('party', filt.party.trim());
    if (filt.due_from) p.set('due_from', filt.due_from);
    if (filt.due_to) p.set('due_to', filt.due_to);
    const t = setTimeout(() => {
      api.get('/finance/pdc/party-summary?' + p.toString(), H())
        .then(r => setSummary(r.data.data || { parties: [], total: null, party_count: 0 }))
        .catch(() => setSummary({ parties: [], total: null, party_count: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [filt.party, filt.due_from, filt.due_to, company, H]);
  useEffect(() => { api.get('/finance/pdc/payees', H()).then(r => setPayees(r.data.data || [])).catch(() => {}); }, [H]);

  // Add a new canonical payee to the shared master (keeps the picklist growable
  // without free-text drift on the cheque).
  // In-page, not window.prompt (2026-08-26) — same reasoning as the copy in Requests.js. A
  // suppressed dialog returns null exactly as Cancel does, so the payee silently was not added
  // and nobody could tell; and asking for a name with no sight of the master is how one party
  // ends up in the register twice under two spellings. Pre-filled from the party name already
  // typed on the cheque, with anything it might already be shown before it is created.
  // Purchase invoices for the chosen payee — the anchor list for the cheque date.
  const [invoices, setInvoices] = useState([]);
  const [newPayee, setNewPayee] = useState(null);
  const openAddPayee = () => setNewPayee({ name: (modal?.party_name || '').trim(), busy: false });
  const confirmAddPayee = async () => {
    const name = (newPayee?.name || '').trim();
    if (!name) { setErr('Enter the payee name.'); return; }
    setNewPayee(n => ({ ...n, busy: true }));
    try {
      const p = (await api.post('/finance/pdc/payees', { name }, H())).data.data;
      setPayees(ps => ps.some(x => x.id === p.id) ? ps : [...ps, p].sort((a, b) => a.name.localeCompare(b.name)));
      setModal(m => ({ ...m, payee_id: p.id, party_name: p.name }));
      setNewPayee(null); setErr('');
    } catch (e) { setErr(e.response?.data?.error || 'Failed to add payee'); setNewPayee(n => n && ({ ...n, busy: false })); }
  };

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      const body = {
        cheque_no: modal.cheque_no, party_name: modal.party_name, payee_id: modal.payee_id || null, amount: modal.amount,
        bank_account: modal.bank_account || null, issue_date: modal.issue_date || null,
        due_date: modal.due_date, remarks: modal.remarks || null,
        // The invoice this cheque pays. Stored so the anchor date stays recoverable.
        po_no: modal.po_no || null,
      };
      if (modal.id) await api.put('/finance/pdc/' + modal.id, body, H());
      else await api.post('/finance/pdc', body, H());
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Open the stop-payment letter PDF (auth + company header can't ride a plain window.open,
  // so fetch it as a blob with H() and open the object URL).
  const openStopLetter = async (row) => {
    try {
      const pdf = await api.get('/finance/pdc/' + row.id + '/stop-letter', { ...H(), responseType: 'blob' });
      window.open(URL.createObjectURL(pdf.data), '_blank');
    } catch { alert('Could not open the stop-payment letter.'); }
  };
  // Stop a Pending cheque: mandatory reason → mark stopped (soft, audited) → open the letter.
  const stopCheque = async (row) => {
    const reason = askReason(`Stop payment on cheque ${row.cheque_no} (${inr(row.amount)} to ${row.party_name})?\nThis marks it Stop-Requested (kept with an audit trail, dropped from live payables) and generates a bank stop-payment letter to print & sign.\n\nReason (required):`);
    if (reason == null) return;
    try {
      await api.post('/finance/pdc/' + row.id + '/stop', { stop_reason: reason }, H());
      await openStopLetter(row);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Stop failed'); }
  };

  // RETIRING A CHEQUE NAMES SOMEBODY (ruling 25, mig 345). Cancelled and Bounced used to be set
  // here with no actor and no reason, while the Stop button three lines down has always demanded
  // both — and the unnamed one is how Rs 22,92,210 moved between two companies' books in August.
  const setStatus = async (row, status) => {
    if (status === row.status) return;
    const RETIRING = status === 'Cancelled' || status === 'Bounced';
    let reason = '', advice = '';
    if (RETIRING) {
      reason = askReason(status === 'Bounced'
        ? `Cheque ${row.cheque_no} (${inr(row.amount)} to ${row.party_name}) BOUNCED.\n\nWhat does the bank's advice say? (required — a bounce is the bank's act, and its reason is what makes it evidence)`
        : `Cancel cheque ${row.cheque_no} (${inr(row.amount)} to ${row.party_name})?\n\nWhy? (required — cancelling is the act that can move a payable between two companies' books, so it cannot be anonymous)`);
      if (reason == null) return;
      advice = window.prompt('Bank advice / return reference, if you have one (optional):') || '';
    } else if (!window.confirm(`Mark cheque ${row.cheque_no} as ${status}?`)) return;
    try {
      await api.patch('/finance/pdc/' + row.id + '/status',
        { status, retire_reason: reason.trim() || undefined, bank_advice_ref: advice.trim() || undefined }, H());
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  // THE ANSWER FROM THE BANK — the state the stop path never had. `stop_requested` recorded that we
  // asked; nothing recorded whether the bank acted, so a stop that worked and a stop that failed
  // read identically. One HAS failed: RSR 95402, stopped 28 July for "wrong date", cleared the next
  // day. Four Oriental cheques, Rs 25,47,089, are still waiting on this answer.
  const confirmStop = async (row) => {
    if (!window.confirm(`Did the bank CONFIRM the stop on cheque ${row.cheque_no} (${inr(row.amount)})?\n\nThis records that the money did NOT leave. It is terminal.`)) return;
    const advice = window.prompt('Bank advice reference, if you have one (optional):') || '';
    try {
      await api.post('/finance/pdc/' + row.id + '/stop-confirm', { bank_advice_ref: advice.trim() || undefined }, H());
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const failedStop = async (row) => {
    const note = askReason(`The stop on cheque ${row.cheque_no} FAILED and the money left?\n\nThis records it as Cleared, keeping the failed stop request visible.\n\nWhat happened? (required)`);
    if (note == null) return;
    try {
      await api.post('/finance/pdc/' + row.id + '/stop-failed', { reason: note.trim() }, H());
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete cheque ${row.cheque_no}? (Prefer Cancelled to keep the audit trail.)`)) return;
    try { await api.delete('/finance/pdc/' + row.id, H()); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  // reminder = due − 15 days (mirrors the stored generated column; shown even before save)
  const reminderOf = (row) => row.reminder_date || (row.due_date ? new Date(new Date(row.due_date).getTime() - 15 * 864e5).toISOString().slice(0, 10) : null);

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };

  return (
    <div>
      {/* ── RULING 35's COUNTER, PUBLISHED ────────────────────────────────────────────────────
          A control that would refuse live work ships disabled WITH a counter, and a counter nobody
          can see is the same as no counter. This says what the rule is, that it is not yet in force,
          what turning it on would cost today, and what turns it on — so the deferral cannot become
          permanent by nobody noticing. It disappears by itself when the number reaches zero. */}
      {approvalStatus && (() => {
        const over = (approvalStatus.data || []).filter(x => x.over_threshold);
        const n = over.reduce((a, x) => a + Number(x.cheques || 0), 0);
        const v = over.reduce((a, x) => a + Number(x.value || 0), 0);
        const th = (approvalStatus.thresholds || [])[0];
        // THE SCOPE GOES ON THE NUMBER, not in a document. The threshold is per company, so the
        // counter is per company and so is enforcement — RSR reaching zero should enable RSR. But a
        // bare "79 cheques" reads as a total, and the person who will misread it is looking at a
        // coloured box, not at docs/design. Same lesson as payableWindow: a caveat nobody reads is
        // not a caveat. The book is named from the switcher, and the sentence says THIS book.
        const book = (companies.find(c => c.id === company) || {}).name
                  || (approvalStatus.data || [])[0]?.company || 'this company';
        if (!n) return (
          <div style={{ border: '1px solid #BFE3D2', background: '#EAF7F1', borderRadius: 8,
                        padding: '10px 13px', marginBottom: 14, fontSize: 12.5, color: '#14684D' }}>
            <strong>Every {book} payment over {th ? inr(th.threshold_amount) : 'the threshold'} is
            approved.</strong>{' '}
            Payment approval can now be made mandatory <strong>for {book}</strong> — the condition for
            enforcing it on this book is this number being zero, and it is. Each company's threshold
            and each company's counter stand alone, so the other book may not be ready yet.
          </div>
        );
        return (
          <div style={{ border: '1px solid #E8D9B0', background: '#FDF8EC', borderRadius: 8,
                        padding: '11px 13px', marginBottom: 14, fontSize: 12.5, color: '#6B5418', lineHeight: 1.55 }}>
            <strong>Payment approval is recorded but NOT yet required.</strong>{' '}
            If it were required today, <strong>{n} {book} cheque{n === 1 ? '' : 's'} worth {inr(v)}</strong>{' '}
            at or over the {th ? inr(th.threshold_amount) : '—'} threshold would be refused.
            {' '}<em>This is {book}'s figure alone — the other book has its own threshold and its own
            count.</em>
            {th && <> The threshold was set by {th.set_by_name || '—'} on {fmtD(th.effective_from)}.</>}
            <div style={{ marginTop: 5, color: '#8A6F2A' }}>
              It is not enforced because approving requires a personal login and payments are recorded
              from a shared one. <strong>Enforcement turns on for {book} when this number reaches
              zero</strong> — which is the same moment every person who handles {book}'s payments has
              their own login.
            </div>
          </div>
        );
      })()}
      {err && <div style={{ border: '1px solid #F3C4C4', background: '#FDECEC', color: '#9B1C1C',
                            borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 13, lineHeight: 1.5 }}>{err}</div>}
      {/* Switcher + New */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: 11, color: '#888', fontWeight: 600, marginRight: 6, textTransform: 'uppercase' }}>Company</span>
          <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => { setErr(''); setModal({ ...emptyForm }); }}
          style={{ padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
          + New PDC
        </button>
      </div>

      {/* ── CHEQUE DATE CALCULATOR ─────────────────────────────────────────────────
          Invoice date + days = cheque date. The arithmetic is trivial; the reason this is a
          panel rather than a mental sum is the PREFILL — the payee's own agreed terms, so the
          user confirms a number instead of remembering one.

          CALENDAR DAYS, NOT WORKING DAYS (ruled). There is no weekend rule in the register to
          encode: of 131 cheques, 15 fall on a Sunday and Saturday is the single most common
          due day (40). Skipping weekends would invent a practice the data contradicts.

          THE OVERRIDE IS THE POINT. Only 48 of 142 payees resolve to a supplier with terms, so
          the prefill answers a minority of cases — and where it does, what the user types
          INSTEAD is the real agreement. The field stays editable and the basis is shown, so a
          deviation is visible rather than silent. */}
      <div style={{ marginBottom: 14, border: '1px solid #E5E3F5', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ background: '#EEEDF8', padding: '7px 13px', fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1 }}>
          Cheque date calculator
        </div>
        <div style={{ background: '#fff', padding: 13, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: 200 }}>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>Party (for terms)</label>
            <select value={calc.payee_id}
              onChange={async e => {
                const payee_id = e.target.value;
                setCalc(c => ({ ...c, payee_id, basis: '', supplier: '', ...(c.touched ? {} : { days: '' }) }));
                if (!payee_id) return;
                try {
                  const r = await api.get('/finance/pdc/terms?payee_id=' + encodeURIComponent(payee_id), H());
                  const d = r.data.data || {};
                  // Never overwrite a figure the user has already typed — the override wins.
                  setCalc(c => {
                    if (c.touched || d.days == null) return { ...c, basis: d.basis, supplier: d.supplier_name || '' };
                    // A fresh prefill sets the days and therefore drives the cheque date.
                    const days = String(d.days);
                    return { ...c, basis: d.basis, supplier: d.supplier_name || '', days, drive: 'days',
                             cheque_date: calcChequeDate(c.invoice_date, days) || '' };
                  });
                } catch { setCalc(c => ({ ...c, basis: 'lookup_failed' })); }
              }}
              style={{ width: '100%', padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
              <option value="">— none —</option>
              {payees.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>Invoice date</label>
            {/* The anchor. Moving it re-derives whichever side is currently the OUTPUT and
                leaves the side the user typed alone — otherwise changing the invoice date
                would silently rewrite the number they had just entered. */}
            <input type="date" value={calc.invoice_date}
              onChange={e => setCalc(c => {
                const invoice_date = e.target.value;
                return c.drive === 'days'
                  ? { ...c, invoice_date, cheque_date: calcChequeDate(invoice_date, c.days) || '' }
                  : { ...c, invoice_date, days: (() => { const n = daysBetween(invoice_date, c.cheque_date); return n == null ? '' : String(n); })() };
              })}
              style={{ padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
          </div>
          <div style={{ width: 110 }}>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>Days</label>
            <input type="number" min="0" value={calc.days}
              onChange={e => setCalc(c => ({ ...c, days: e.target.value, touched: true, drive: 'days',
                                             cheque_date: calcChequeDate(c.invoice_date, e.target.value) || '' }))}
              style={{ width: '100%', padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13,
                       background: calc.drive === 'days' ? '#fff' : '#F6F5FB' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>Cheque date</label>
            {/* Also an INPUT. Typing here computes the days instead — the register could never
                answer "how many days is this cheque", and that is the half people ask about a
                cheque already written. */}
            <input type="date" value={calc.cheque_date}
              onChange={e => setCalc(c => ({ ...c, cheque_date: e.target.value, touched: true, drive: 'cheque_date',
                                             days: (() => { const n = daysBetween(c.invoice_date, e.target.value); return n == null ? '' : String(n); })() }))}
              style={{ padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13,
                       background: calc.drive === 'cheque_date' ? '#fff' : '#F6F5FB' }} />
          </div>
          {calc.cheque_date && calc.invoice_date && (
            <div style={{ background: '#EEEDF8', borderRadius: 8, padding: '8px 16px', minWidth: 200 }}>
              <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
                {calc.drive === 'days' ? 'Cheque date' : 'Terms implied'}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#3B3A8F' }}>
                {calc.drive === 'days' ? fmtLong(calc.cheque_date) : `${calc.days} days`}
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                {calc.drive === 'days' ? `${calc.days || 0} days from the invoice` : fmtLong(calc.cheque_date)}
              </div>
              {/* A negative span is a cheque dated BEFORE its invoice. 5 such rows already exist
                  in the register, so this is a real state and is named rather than hidden. */}
              {Number(calc.days) < 0 && (
                <div style={{ fontSize: 11, color: '#A32D2D', fontWeight: 700, marginTop: 3 }}>
                  dated before the invoice
                </div>
              )}
            </div>
          )}
          {calc.basis && (
            <div style={{ flex: 1, minWidth: 210, fontSize: 12, color: calc.basis === 'supplier_terms' ? '#166534' : '#854F0B' }}>
              {calc.basis === 'supplier_terms'
                ? <>Prefilled from <strong>{calc.supplier}</strong>&rsquo;s agreed terms{calc.touched ? ' — you have changed it.' : '.'}</>
                : calc.basis === 'supplier_has_no_terms'
                ? <>{calc.supplier} has no payment terms recorded — enter the days.</>
                : calc.basis === 'lookup_failed'
                ? <>Could not read the party&rsquo;s terms — enter the days.</>
                : <>This party is not linked to a vendor, so there are no terms to prefill. Enter the days.</>}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filt.status} onChange={e => setFilt(f => ({ ...f, status: e.target.value }))} style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
          <option value="">All statuses</option>
          {['Pending', 'Cleared', 'Bounced', 'Cancelled', 'stop_requested', 'stopped'].map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
        </select>
        <input placeholder="Party contains…" value={filt.party} onChange={e => setFilt(f => ({ ...f, party: e.target.value }))} style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: '#888' }}>Due</span>
        <input type="date" value={filt.due_from} onChange={e => setFilt(f => ({ ...f, due_from: e.target.value }))} style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: '#888' }}>→</span>
        <input type="date" value={filt.due_to} onChange={e => setFilt(f => ({ ...f, due_to: e.target.value }))} style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
        {(filt.status || filt.party || filt.due_from || filt.due_to) &&
          <button onClick={() => setFilt({ status: '', party: '', due_from: '', due_to: '' })} style={{ padding: '6px 12px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Clear</button>}
      </div>

      {/* Party-exposure summary. Party filtered → the single-party strip(s). Unfiltered → the same
          four tiles TOTALLED across all parties, then the party-wise table (click a row to drill in). */}
      {filt.party.trim()
        ? summary.parties.map(g => <PartyStrip key={g.party_label} g={g} />)
        : (summary.total && (summary.party_count > 0) && (
            <>
              <PartyStrip g={{ ...summary.total, party_label: `All parties · ${summary.party_count} part${summary.party_count === 1 ? 'y' : 'ies'}` }} />
              <PartyTable parties={summary.parties} onPick={(name) => setFilt(f => ({ ...f, party: name }))} />
            </>
          ))}

      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {['Cheque No', 'Party', 'Amount', 'Bank', 'Company', 'Issue', 'Due', 'Reminder', 'Status', 'Approval', ''].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No cheques.</td></tr>
              : rows.map(r => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.Pending;
                return (
                  <tr key={r.id}>
                    <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{r.cheque_no}</td>
                    <td style={td}>{r.party_name}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{inr(r.amount)}</td>
                    <td style={td}>{r.bank_account || '—'}</td>
                    <td style={td}>{r.company_code || r.company_name || '—'}</td>
                    <td style={td}>{fmtD(r.issue_date)}</td>
                    <td style={{ ...td, fontWeight: 600, color: r.status === 'Pending' && r.days_to_due < 0 ? '#A32D2D' : '#333' }}>{fmtD(r.due_date)}</td>
                    <td style={{ ...td, color: '#888' }}>{fmtD(reminderOf(r))}</td>
                    <td style={td}><span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: st.bg, color: st.c }}>{STATUS_LABEL[r.status] || r.status}</span>
                      {r.status === 'stop_requested' && r.stop_reason && <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3, maxWidth: 200 }}>⛔ {r.stop_reason}</div>}</td>
                    {/* RULING 24. Shown on every row, including the 162 that predate the act —
                        "Not approved" is the honest reading and hiding it would make an absent
                        control look like a satisfied one. */}
                    <td style={td}>
                      {(() => { const a = approvalOf(r.approval_kind); return (
                        <span title={a.hint} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10.5,
                                     fontWeight: 700, background: a.bg, color: a.c, whiteSpace: 'nowrap' }}>{a.label}</span>); })()}
                      {r.linked_request_no && <div style={{ fontSize: 10, color: '#777', marginTop: 3 }}>{r.linked_request_no}</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Approving is management / finance_approver, not accounts — accounts is the
                          desk that executes. The button is offered only where it would succeed, and
                          the refusal it would otherwise hit is the segregation sentence. */}
                      {r.approval_kind === 'unapproved' && canApprove &&
                        <button onClick={() => approve(r)} title="Record that you authorise this payment"
                          style={{ padding: '3px 9px', background: '#E3F3E6', border: 'none', borderRadius: 4, color: '#1E6B33', cursor: 'pointer', fontSize: 11, fontWeight: 700, marginRight: 4 }}>✓ Approve</button>}
                      <select value="" onChange={e => { if (e.target.value) setStatus(r, e.target.value); }} title="Change status"
                        style={{ padding: '3px 6px', border: '1px solid #ddd', borderRadius: 4, fontSize: 11, marginRight: 4 }}>
                        <option value="">Status…</option>
                        {['Pending', 'Cleared', 'Bounced', 'Cancelled'].filter(s => s !== r.status).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {r.status === 'Pending' &&
                        <button onClick={() => stopCheque(r)} title="Stop payment — mark stopped + generate the bank stop-payment letter"
                          style={{ padding: '3px 9px', background: '#FCE7E7', border: 'none', borderRadius: 4, color: '#A32D2D', cursor: 'pointer', fontSize: 11, fontWeight: 700, marginRight: 4 }}>⛔ Stop</button>}
                      {r.status === 'stop_requested' &&
                        <button onClick={() => openStopLetter(r)} title="Re-print the stop-payment letter"
                          style={{ padding: '3px 9px', background: '#EEEDF8', border: 'none', borderRadius: 4, color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 4 }}>📄 Letter</button>}
                      {/* THE BANK'S ANSWER. Until mig 345 there was nowhere to put it, so twelve
                          requests sat unresolved and a failed stop was invisible. */}
                      {r.status === 'stop_requested' && <>
                        <button onClick={() => confirmStop(r)} title="The bank confirmed the stop — the money did not leave. Terminal."
                          style={{ padding: '3px 9px', background: '#E3F3E6', border: 'none', borderRadius: 4, color: '#1E6B33', cursor: 'pointer', fontSize: 11, fontWeight: 700, marginRight: 4 }}>✓ Stopped</button>
                        <button onClick={() => failedStop(r)} title="The stop failed and the cheque cleared anyway"
                          style={{ padding: '3px 9px', background: '#FDECEA', border: 'none', borderRadius: 4, color: '#A33', cursor: 'pointer', fontSize: 11, fontWeight: 700, marginRight: 4 }}>✗ Stop failed</button>
                      </>}
                      <button onClick={() => { setErr(''); setModal({ ...r, issue_date: r.issue_date ? r.issue_date.slice(0, 10) : '', due_date: r.due_date ? r.due_date.slice(0, 10) : '' }); }}
                        style={{ padding: '3px 9px', background: '#EEEDF8', border: 'none', borderRadius: 4, color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 4 }}>Edit</button>
                      <button onClick={() => del(r)} style={{ padding: '3px 9px', background: '#FCEBEB', border: 'none', borderRadius: 4, color: '#E22D2D', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✕</button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <ListFooter shown={rows.length} total={rows.length} noun="cheques" />

      {/* Add / Edit modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, width: 560, maxWidth: '92vw' }}>
            <div style={{ background: '#2D2C72', padding: '14px 22px', borderRadius: '12px 12px 0 0' }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{modal.id ? 'Edit PDC' : 'New PDC'}</h3>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={labelStyle}>Cheque No *</label><input style={inputStyle} value={modal.cheque_no} onChange={e => setModal(m => ({ ...m, cheque_no: e.target.value }))} /></div>
                <div><label style={labelStyle}>Amount *</label><input type="number" step="0.01" style={inputStyle} value={modal.amount} onChange={e => setModal(m => ({ ...m, amount: e.target.value }))} /></div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Payee *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select style={{ ...inputStyle, flex: 1 }}
                      value={modal.payee_id || (payees.find(p => p.name === modal.party_name)?.id) || ''}
                      onChange={async e => {
                        const p = payees.find(x => x.id === e.target.value);
                        setModal(m => ({ ...m, payee_id: p ? p.id : null, party_name: p ? p.name : m.party_name,
                                         terms_basis: '', terms_supplier: '' }));
                        if (!p) return;
                        // PREFILL FROM THE PARTY'S OWN TERMS (ruled 2026-08-31). The cheque and the
                        // record are the same act here — 104 of 113 PDCs were entered on their own
                        // issue date, average lag 0.1 days — so the due date IS being decided at this
                        // moment, and the agreed terms are the right opening answer.
                        //
                        // NEVER OVERWRITES A TYPED DATE. If the user has already set a due date, the
                        // prefill reports the terms and leaves the date alone; picking the payee must
                        // not silently move a figure somebody entered.
                        try {
                          const r = await api.get('/finance/pdc/terms?payee_id=' + encodeURIComponent(p.id), H());
                          const d = r.data.data || {};
                          setModal(m => {
                            const next = { ...m, terms_basis: d.basis, terms_supplier: d.supplier_name || '',
                                           terms_days: d.days == null ? '' : String(d.days) };
                            if (d.days == null || m.due_date) return next;
                            // Anchor on the invoice date when one has been given, else the issue date.
                            // Terms run from the INVOICE, so the invoice date is the correct anchor and
                            // the issue date is a stand-in that is named in the hint below.
                            const anchor = m.invoice_date || m.issue_date;
                            if (!anchor) return next;
                            return { ...next, due_date: calcChequeDate(anchor, d.days) || '' };
                          });
                        } catch { setModal(m => ({ ...m, terms_basis: 'lookup_failed' })); }
                        // and the invoices that could anchor the cheque date
                        try {
                          const inv = await api.get('/finance/pdc/invoices?payee_id=' + encodeURIComponent(p.id), H());
                          setInvoices(inv.data.data || []);
                        } catch { setInvoices([]); }
                      }}>
                      <option value="">— select payee —</option>
                      {payees.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button type="button" onClick={openAddPayee} style={{ padding: '0 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>＋ New</button>
                  </div>
                  {newPayee && (() => {
                    const hits = nearMatches(newPayee.name, payees);
                    return (
                      <div style={{ marginTop: 8, border: '1px solid #E3E2F0', background: '#F7F7FB', borderRadius: 6, padding: '10px 12px' }}>
                        <label style={labelStyle}>New payee name — added to the shared master</label>
                        <input autoFocus style={inputStyle} value={newPayee.name}
                          onChange={e => setNewPayee(n => ({ ...n, name: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter' && !newPayee.busy) confirmAddPayee(); }}
                          placeholder="Exactly as it should appear on the cheque" />
                        {hits.length > 0 && (
                          <div style={{ marginTop: 8, background: '#FFF7ED', border: '1px solid #E0A030', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#7C4A03' }}>
                            <strong>Already in the master{hits.length > 1 ? ' (closest first)' : ''}:</strong>
                            {hits.map(h => (
                              <div key={h.item.id} style={{ marginTop: 4 }}>
                                <button type="button" onClick={() => { setModal(m => ({ ...m, payee_id: h.item.id, party_name: h.name })); setNewPayee(null); }}
                                  style={{ background: '#fff', border: '1px solid #E0A030', borderRadius: 5, padding: '3px 9px', cursor: 'pointer', fontSize: 12, color: '#7C4A03', fontWeight: 600 }}>
                                  use “{h.name}”
                                </button>
                                <span style={{ marginLeft: 6, color: '#9a6a1a' }}>{h.why}</span>
                              </div>
                            ))}
                            <div style={{ marginTop: 6 }}>Add it anyway only if this is a different party.</div>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button type="button" disabled={newPayee.busy} onClick={confirmAddPayee}
                            style={{ padding: '7px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: newPayee.busy ? 'wait' : 'pointer', opacity: newPayee.busy ? 0.6 : 1 }}>
                            {newPayee.busy ? 'Adding…' : 'Add payee'}
                          </button>
                          <button type="button" onClick={() => setNewPayee(null)}
                            style={{ padding: '7px 16px', background: '#f1f1f4', border: 'none', borderRadius: 6, fontSize: 12.5, cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    );
                  })()}
                  {modal.party_name && !payees.some(p => p.id === modal.payee_id) && <div style={{ fontSize: 11, color: '#9A6B00', marginTop: 3 }}>Legacy party: “{modal.party_name}” — pick a payee to link it.</div>}
                </div>
                <div>
                  <label style={labelStyle}>Bank (→ company)</label>
                  <select style={inputStyle} value={modal.bank_account || ''} onChange={e => setModal(m => ({ ...m, bank_account: e.target.value }))}>
                    <option value="">— select bank —</option>
                    {banks.map(b => <option key={b.id} value={b.bank_code}>{b.bank_code} · {b.bank_name || b.company_name}{b.is_default ? ' (default)' : ''}</option>)}
                  </select>
                </div>
                {/* AGAINST INVOICE — the anchor the terms run from, and the reason no
                    invoice_date column is needed. Picking an invoice stores its po_no (a column
                    that has existed since the register shipped and is NULL on all 131 rows), so
                    the invoice date stays RECOVERABLE afterwards instead of being duplicated
                    onto the cheque. The 131 that came before will still be mute; every cheque
                    written this way will not. */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Against invoice</label>
                  <select style={inputStyle} value={modal.po_no || ''}
                    onChange={e => {
                      const inv = invoices.find(x => x.po_no === e.target.value);
                      setModal(m => {
                        const next = { ...m, po_no: inv ? inv.po_no : '',
                                       invoice_date: inv && inv.invoice_date ? String(inv.invoice_date).slice(0, 10) : '' };
                        // The invoice's own terms beat the supplier default when it carries them.
                        const days = (inv && /^\d+$/.test(String(inv.payment_terms || '').trim()))
                          ? String(parseInt(inv.payment_terms, 10)) : next.terms_days;
                        if (!inv || !days || m.due_date_touched) return { ...next, terms_days: days };
                        return { ...next, terms_days: days, due_date: calcChequeDate(next.invoice_date, days) || m.due_date };
                      });
                    }}>
                    <option value="">— none (cheque not against a specific invoice) —</option>
                    {invoices.map(i => (
                      <option key={i.po_no || i.invoice_no} value={i.po_no || ''}>
                        {i.invoice_no} · {fmtD(i.invoice_date)} · {inr(i.total_amount)}{i.po_no ? '' : '  (no PO ref — cannot link)'}
                      </option>
                    ))}
                  </select>
                  {modal.payee_id && invoices.length === 0 && (
                    <div style={{ fontSize: 11, color: '#854F0B', marginTop: 3 }}>
                      No purchase invoices found for this party — the cheque date will anchor on the issue date.
                    </div>
                  )}
                </div>
                <div><label style={labelStyle}>Issue Date</label><input type="date" style={inputStyle} value={modal.issue_date || ''}
                  onChange={e => setModal(m => {
                    const issue_date = e.target.value;
                    // Only a stand-in anchor: if there is no invoice date and nothing typed, the
                    // terms run from here so the field is not left blank.
                    if (!m.terms_days || m.invoice_date || m.due_date_touched) return { ...m, issue_date };
                    return { ...m, issue_date, due_date: calcChequeDate(issue_date, m.terms_days) || m.due_date };
                  })} /></div>
                <div>
                  <label style={labelStyle}>Due Date *</label>
                  <input type="date" style={inputStyle} value={modal.due_date || ''}
                    onChange={e => setModal(m => ({ ...m, due_date: e.target.value, due_date_touched: true }))} />
                  {/* Says where the date came from, and stops saying it the moment the user
                      overrides — an override is the real agreement and should not be dressed up
                      as the vendor's terms. */}
                  {modal.terms_basis && (() => {
                    const anchor = modal.invoice_date || modal.issue_date;
                    const span = daysBetween(anchor, modal.due_date);
                    if (modal.terms_basis === 'supplier_terms') return (
                      <div style={{ fontSize: 11, marginTop: 3, color: modal.due_date_touched ? '#854F0B' : '#166534' }}>
                        {modal.due_date_touched
                          ? <>Overridden — <strong>{span == null ? 'set by hand' : `${span} days`}</strong> against{' '}
                              {modal.terms_supplier}&rsquo;s agreed <strong>{modal.terms_days}</strong>
                              {span != null && Number(modal.terms_days) ? ` · ${span - Number(modal.terms_days) > 0 ? '+' : ''}${span - Number(modal.terms_days)} days` : ''}.
                              {' '}The terms are the agreement; this is the practice.</>
                          : <>{modal.terms_days} days from the {modal.invoice_date ? 'invoice' : 'issue'} date, per {modal.terms_supplier}&rsquo;s terms.
                              {!modal.invoice_date && ' Pick the invoice if the terms run from it.'}</>}
                      </div>
                    );
                    return (
                      <div style={{ fontSize: 11, marginTop: 3, color: '#854F0B' }}>
                        {modal.terms_basis === 'no_supplier_link' ? 'This party is not linked to a vendor — no terms to prefill.'
                         : modal.terms_basis === 'supplier_has_no_terms' ? `${modal.terms_supplier} has no payment terms on file.`
                         : 'Could not read the party\u2019s terms.'}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <label style={labelStyle}>Reminder (auto)</label>
                  <input style={{ ...inputStyle, background: '#F7F6F3', color: '#888' }} value={fmtD(reminderOf(modal))} readOnly />
                </div>
                <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Remarks</label><textarea style={{ ...inputStyle, minHeight: 52, resize: 'vertical' }} value={modal.remarks || ''} onChange={e => setModal(m => ({ ...m, remarks: e.target.value }))} /></div>
              </div>
              {err && <div style={{ marginTop: 12, color: '#A32D2D', fontSize: 13 }}>{err}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
                <button onClick={() => setModal(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button disabled={saving} onClick={save} style={{ padding: '8px 20px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving…' : (modal.id ? 'Update' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
